import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { ConfirmDialog } from './confirm-dialog';

const schema = z.object({
  password: z.string().min(1, 'mfa_disable_password_required'),
});

export interface DisableMfaDialogProps {
  open: boolean;
  submitting: boolean;
  inlineError: string | null;
  onSubmit: (password: string) => void;
  onCancel: () => void;
}

export function DisableMfaDialog({
  open,
  submitting,
  inlineError,
  onSubmit,
  onCancel,
}: DisableMfaDialogProps) {
  const { t } = useTranslation('security');
  const formId = useId();
  const passwordId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setValidationError(null);
      // Auto-focus the password input
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = schema.safeParse({ password });
    if (!r.success) {
      setValidationError(r.error.issues[0]?.message ?? 'mfa_disable_password_required');
      return;
    }
    setValidationError(null);
    onSubmit(r.data.password);
  }

  return (
    <ConfirmDialog
      open={open}
      title={t('mfa_disable_confirm_title')}
      subtitle={t('mfa_disable_confirm_subtitle')}
      submitLabel={t('mfa_disable_submit')}
      cancelLabel={t('mfa_disable_cancel')}
      submitting={submitting}
      variant="danger"
      formId={formId}
      onCancel={onCancel}
    >
      <form id={formId} onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.5rem' }}>
        <FieldRow>
          <Label htmlFor={passwordId}>{t('mfa_disable_password_label')}</Label>
          <Input
            ref={inputRef}
            id={passwordId}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="mfa-disable-password"
          />
          <FieldError
            message={
              validationError
                ? t(validationError, t('mfa_disable_password_label'))
                : (inlineError ?? undefined)
            }
          />
        </FieldRow>
      </form>
    </ConfirmDialog>
  );
}
